'use client';

import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { KST, parseKst } from '../utils';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

interface UseDdayCountdownResult {
  /** 표시 라벨. 24h 이상 = "D-N", 24h 미만 = "HH:MM"(리스트는 분까지만), 마감/입력 없음 = 빈 문자열 */
  label: string;
  /** 마감 도달 또는 입력 없음 */
  isExpired: boolean;
  /** 24h 미만 남음 (강조 표시용) */
  isUrgent: boolean;
}

/**
 * 마감 시각까지의 D-day / 라이브 카운트다운 라벨을 반환한다.
 *
 * - 24h 이상 → "D-N" (마감일 자정 기준 캘린더 일수 차. 예: 오늘 6/10, 마감 6/17 → D-7)
 * - 24h 미만 → "HH:MM"(리스트는 분까지만). 1초마다 `now` 갱신(분 경계/만료 정확도용)
 * - 마감 도달 → label="" + isExpired=true (호출부에서 fallback 라벨 결정)
 *
 * NOTE(SID-4162): D-N 은 잔여 ms 의 `Math.ceil` 이 아니라 캘린더 날짜 차이로 계산한다.
 * 마감 시각이 보통 당일 23:59:59 이므로 `ceil` 은 partial-day 만큼 +1 overshoot 했다
 * (6/10 → 6/17 마감을 D-8 로 표시). 자정 기준 `diff('day')` 로 정확한 D-7 을 낸다.
 *
 * NOTE(SID-4180): 마감 시각/캘린더 자정을 모두 KST(Asia/Seoul) 로 고정한다. BE 가 내려주는
 * `"YYYY-MM-DD HH:mm:ss"` 에는 timezone 표기가 없어 `dayjs(value)` 는 실행환경 TZ(서버 UTC 등)
 * 로 해석했다. KST 보다 뒤처진 TZ 에서는 당일 23:59:59 마감 캠페인이 실제 KST 자정 이전인데도
 * isExpired=true 로 떨어져 '모집 종료' 로 오노출됐다 (이슈A). `parseKst` 로 마감을 KST 절대시각으로,
 * `dayjs(now).tz(KST).startOf('day')` 로 "오늘" 을 KST 자정으로 고정해 두 문제를 함께 해소한다.
 *
 * SSR-safe: 초기 `now=null` 로 두어 서버 렌더와 클라이언트 hydration 결과 일치 (둘 다 빈 라벨).
 * mount 후 `setNow(Date.now())` 로 실제 시각 반영.
 *
 * `now` 를 state 로 둔 이유 — 무의미한 counter 로 force-rerender 하는 것보다
 * "현재 시각" 자체를 state 의미로 보존하면 diff 계산이 단일 값으로 일관된다.
 */
export const useDdayCountdown = (deadlineAt: string | null | undefined): UseDdayCountdownResult => {
  const [now, setNow] = useState<number | null>(null);

  useEffect(() => {
    setNow(Date.now());
    if (!deadlineAt) return;
    const deadlineMs = parseKst(deadlineAt).valueOf();
    const initialDiff = deadlineMs - Date.now();
    if (initialDiff <= 0 || initialDiff >= ONE_DAY_MS) return;

    const id = setInterval(() => {
      const next = Date.now();
      setNow(next);
      if (deadlineMs - next <= 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [deadlineAt]);

  if (!deadlineAt || now === null) {
    return { label: '', isExpired: !deadlineAt, isUrgent: false };
  }

  const deadline = parseKst(deadlineAt);
  const diffMs = deadline.valueOf() - now;
  if (diffMs <= 0) return { label: '', isExpired: true, isUrgent: false };

  if (diffMs < ONE_DAY_MS) {
    // 리스트는 분까지만 노출(HH:MM). 초 단위(HH:MM:SS)는 상세(useCountdown)에서만 사용.
    // 1초 tick 은 분 경계 정확도/만료 감지를 위해 유지(라벨은 분 단위로만 변경).
    const h = String(Math.floor(diffMs / (60 * 60 * 1000))).padStart(2, '0');
    const m = String(Math.floor((diffMs % (60 * 60 * 1000)) / (60 * 1000))).padStart(2, '0');
    return { label: `${h}:${m}`, isExpired: false, isUrgent: true };
  }

  // 마감일·오늘 모두 KST 자정 기준으로 캘린더 일수 차를 계산 (실행환경 TZ 무관).
  const days = deadline.startOf('day').diff(dayjs(now).tz(KST).startOf('day'), 'day');
  return { label: `D-${Math.max(days, 0)}`, isExpired: false, isUrgent: false };
};
